import {
    Component,
    ComponentInterface,
    Prop,
    State,
    Element,
    Listen,
    Watch,
} from "@stencil/core";
import api from "../../utils/api";
import serializeForm from "../../utils/serializeForm";
import waitForHydrated from "../../utils/waitForHydrated";
import {
    RequestECatalogItem,
    RequestECatalogResponse,
} from "./request-e-catalog-response";
import row from "./row";
import { debounce } from "../../utils/debounce";

@Component({
    tag: "ux-iws-data-controller-request-e-catalog",
})
export class DataControllerRequestECatalog implements ComponentInterface {
    @Element() element: HTMLUxIwsDataControllerRequestECatalogElement;
    /** endpoint where data can be requested */
    @Prop() url: string = "";
    /** Preselected machine to display on top */
    @Prop() preselectedMachines: string = "";
    /** CSS-style selector for the filter */
    @Prop() filterSelector: string = "";
    /** CSS-style selector for the table */
    @Prop() tableSelector: string = "";
    /** selector of the form element */
    @Prop() formSelector: string = "";
    /** CSS-style selector for the scroll-sensor */
    @Prop() scrollSensorSelector: string = "";
    /** the input name to be given to the hidden inputs added to the form */
    @Prop() inputName: string = "machine";

    /** keeps track of whether the submit button should be disabled (no selcted machines) */
    @State() submitButtonDisabled: boolean = false;

    /** the form element */
    private form: HTMLFormElement | null = null;
    /** element that contains all filters */
    private filter: HTMLUxFilterElement | null = null;
    /** form element for filter */
    private filterForm: HTMLFormElement | null = null;
    /** table element */
    private table: HTMLTableElement | null = null;
    /** table body element where the data should be put */
    private tableBody: HTMLTableSectionElement | null = null;
    /** URL for getting the next page */
    private nextPage: string = "";
    /** DOM element where all the tiles should be put */
    private scrollSensor: HTMLUxInfiniteScrollSensorElement | null = null;

    componentDidLoad() {
        this.setForm();
        this.setFilter();
        this.setTable();
        this.setScrollSensor();
        this.checkFormEmptyState();
        this.requestData(this.url);
        this.contentObserver.observe(this.tableBody, {
            childList: true,
        });
    }

    private checkFormEmptyState() {
        if (!this.form.querySelector("input")) this.submitButtonDisabled = true;
        else this.submitButtonDisabled = false;
    }

    @Watch("submitButtonDisabled")
    handleSubmitButtonState() {
        if (this.submitButtonDisabled === true) {
            this.element
                .closest("ux-modal")
                .querySelectorAll(`button[type="submit"]`)
                .forEach((button) =>
                    button.classList.add("ux-button--disabled")
                );
        } else {
            this.element
                .closest("ux-modal")
                .querySelectorAll(`button[type="submit"]`)
                .forEach((button) =>
                    button.classList.remove("ux-button--disabled")
                );
        }
    }

    private onTableBodyChange = () => {
        const formHiddenInputs = Array.from(
            this.form.querySelectorAll("input")
        ).map((input) => {
            return input.value;
        });
        this.tableBody.querySelectorAll("tr").forEach((row) => {
            const rowCheckbox = row.querySelector("ux-checkbox");
            const value = rowCheckbox.value;
            if (formHiddenInputs.includes(value)) rowCheckbox.checked = true;
        });
    };

    private contentObserver: MutationObserver = new MutationObserver(
        debounce(() => this.onTableBodyChange(), 100)
    );

    private addHiddenInputToForm = (value: string) => {
        if (this.form === null) return;
        if (this.form.querySelector(`input[value="${value}"]`) !== null) return;
        const newInput = document.createElement("input");
        newInput.setAttribute("type", "hidden");
        newInput.setAttribute("name", this.inputName);
        newInput.setAttribute("value", value);
        this.form.appendChild(newInput);
    };

    private removeHiddenInputFromForm = (value: string) => {
        if (this.form === null) return;
        this.form.querySelector(`input[value="${value}"]`).remove();
    };

    @Listen("uxCheckboxChange", { target: "window" })
    setFormElements(e: CustomEvent) {
        if (e.detail.checked === true && e.detail.name === this.inputName)
            this.addHiddenInputToForm(e.detail.value);
        else if (e.detail.checked === false)
            this.removeHiddenInputFromForm(e.detail.value);
        this.checkFormEmptyState();
    }

    /** connect form */
    @Watch("formSelector")
    setForm() {
        if (this.formSelector === "") return;
        this.form = this.element.parentElement.querySelector(
            this.formSelector
        ) as HTMLFormElement;
    }

    /** connect filter */
    @Watch("filterSelector")
    setFilter() {
        if (this.filterSelector === "") return;
        this.filter = this.element.querySelector(
            this.filterSelector
        ) as HTMLUxFilterElement;
        waitForHydrated(this.filter, () => this.setFilterForm());
        this.filter.addEventListener("uxFilterChange", () =>
            this.requestData(this.url)
        );
    }

    private setFilterForm() {
        this.filterForm = this.filter.querySelector("form") as HTMLFormElement;
        this.filterForm?.addEventListener("submit", (e) => e.preventDefault());
    }

    /** connect table */
    @Watch("tableSelector")
    setTable() {
        if (this.tableSelector === "") return;
        this.table = this.element.querySelector(
            this.tableSelector
        ) as HTMLTableElement;
        this.tableBody = this.table?.querySelector("tbody");
    }

    /** connect scroll-sensor */
    @Watch("scrollSensorSelector")
    setScrollSensor() {
        if (this.scrollSensorSelector === "") return;
        this.scrollSensor = this.element.querySelector(
            this.scrollSensorSelector
        ) as HTMLUxInfiniteScrollSensorElement;
        if (this.scrollSensor !== null) {
            this.scrollSensor.addEventListener(
                "uxInfiniteScrollSensorScrolledIntoView",
                () => {
                    this.requestData(this.nextPage);
                }
            );
        }
    }

    /** post filter and sort settings to get data from the endpoint */
    @Watch("url")
    initialRequest() {
        this.requestData(this.url);
    }

    /** Send the real request and handle response */
    private async requestData(url: string) {
        if (url === "") return;
        this.showLoadingSpinner();
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body: serializeForm(this.filterForm),
                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };

        // check for parameters in url and append preselectedMachines
        if (this.preselectedMachines != "")
            url = url.includes("?")
                ? `${url}&preselectedMachines=${this.preselectedMachines}`
                : `${url}?preselectedMachines=${this.preselectedMachines}`;

        try {
            const data = await api<RequestECatalogResponse>(url, options);

            // simulating call time in dev environment
            if (isMockUrl(url))
                await new Promise((resolve) => setTimeout(resolve, 1000));

            if (this.url === url) this.clearExistingData();

            if (data) {
                if (data.items.length > 0) {
                    this.appendItemsToTable(data.items);
                }
                this.nextPage = data.nextPage || "";
                if (this.nextPage !== "" && this.scrollSensor !== null) {
                    this.scrollSensor.hasFired = false;
                }
            }
        } catch (e) {
            console.log(e);
        }

        this.hideLoadingSpinner();
    }

    private showLoadingSpinner() {
        const modalSpinner = document.createElement("ux-spinner-overlay");
        this.element.parentElement.append(modalSpinner);
        modalSpinner.opened = true;
    }

    private hideLoadingSpinner() {
        const spinnerInModal =
            this.element.parentElement.querySelector("ux-spinner-overlay");
        if (spinnerInModal !== null) spinnerInModal.remove();
    }

    private clearExistingData() {
        if (this.tableBody !== null) this.tableBody.textContent = "";
    }

    private appendItemsToTable(items: RequestECatalogItem[]) {
        if (this.tableBody === null) return;
        const rowItems = items.map((item) => ({
            ...item,
            inputName: this.inputName,
        }));
        this.tableBody.insertAdjacentHTML(
            "beforeend",
            rowItems.map(row).join("")
        );
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}
